import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MODULE_IDS } from '@/lib/constants/modules';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { templateLabel } from '@/features/campaigns/types';
import { useTtsAccess } from '@/features/tts-flow/api';
import { TtsFlowPreviewModal } from '@/features/tts-flow/components/TtsFlowPreviewModal';
import { cn } from '@/lib/utils/cn';
import { ensureArray } from '@/lib/utils/ensure-array';
import type { LucideIcon } from 'lucide-react';
import {
  AudioLines,
  BotMessageSquare,
  Eye,
  Hash,
  Infinity as InfinityIcon,
  ListFilter,
  PhoneCall,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTtsFlows, useUserModules } from '../../api';
import {
  flavorOptions,
  TEMPLATE_OPTIONS,
  type TemplateId,
  type VoiceCampaignFormValues,
} from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
  /** Block the template picker when editing an existing campaign. */
  lockTemplate?: boolean;
}

interface TemplateCard {
  value: TemplateId;
  label: string;
  description: string;
  icon: LucideIcon;
}

const TEMPLATE_DETAILS: Record<TemplateId, Omit<TemplateCard, 'value' | 'label'>> = {
  '0': {
    description: 'Single pre-recorded message',
    icon: AudioLines,
  },
  '1': {
    description: 'Menu + DTMF input',
    icon: Hash,
  },
  '2': {
    description: 'Patch to a live agent',
    icon: PhoneCall,
  },
  '3': {
    description: 'Custom IVR flow',
    icon: Workflow,
  },
  '7': {
    description: 'Text-to-speech IVR',
    icon: Sparkles,
  },
  '8': {
    description: 'TTS menu + DTMF',
    icon: BotMessageSquare,
  },
  '9': {
    description: 'TTS menu + agent patch',
    icon: BotMessageSquare,
  },
  '10': {
    description: 'Driven by a TTS Flow',
    icon: InfinityIcon,
  },
};

/** Icon for each TTS filter chip (display only). */
const FLAVOR_ICONS: Record<string, LucideIcon> = {
  '7': AudioLines,
  '8': Hash,
  '9': PhoneCall,
};

export function StepBasics({ values, setValues, lockTemplate = false }: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const modules = useUserModules();
  const noTtsModule = useTtsAccess().reason === 'no-module';
  const ttsFlows = useTtsFlows();

  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );

  // Continuous Campaign is mutually exclusive with the other templates:
  // when the account has that module on, ONLY Continuous shows. Otherwise
  // all standard cards (Simple IVR, DTMF, Call Patch, Custom IVR, TTS) are
  // visible — the backend rejects compose if a required module is missing.
  const isContinuous = moduleNames.has('Continuous Campaign');
  const hasCustomIvrModule = useMemo(
    () =>
      (modules.data ?? []).some(
        (m) => Number(m.moduleId) === MODULE_IDS.IVR || m.moduleName === 'Custom IVR',
      ),
    [modules.data],
  );

  const flowOptions = useMemo(() => {
    const all = ensureArray(ttsFlows.data);
    const templateId = isContinuous ? values.templateIdByTTS : values.templateId;
    return all.filter((r) => String(r.templateId) === String(templateId) && r.status === 1);
  }, [ttsFlows.data, values.templateId, values.templateIdByTTS, isContinuous]);

  /** The flow currently picked in the dropdown (for the confirmation card). */
  const selectedFlow = useMemo(() => {
    const selectedTtsId = isContinuous ? values.selectedTTSFlowId : values.ttsId;
    return flowOptions.find((f) => String(f.id) === String(selectedTtsId));
  }, [flowOptions, values.ttsId, values.selectedTTSFlowId, isContinuous]);

  const availableCards: TemplateCard[] = useMemo(() => {
    const out: TemplateCard[] = [];
    for (const o of TEMPLATE_OPTIONS) {
      if (isContinuous && o.value === '7') continue;
      if (isContinuous && o.value === '3') continue;
      // No Custom IVR module → don't offer it (kept when editing an existing one).
      if (o.value === '3' && !hasCustomIvrModule && !lockTemplate) continue;
      if (!isContinuous && o.value === '10') continue;
      // 8 and 9 are TTS sub-flavors of 7 — only 7 appears in the picker.
      if (o.value === '8' || o.value === '9') continue;
      // No TTS module → don't offer the TTS campaign type (kept when editing an existing one).
      if (o.value === '7' && noTtsModule && !lockTemplate) continue;
      const detail = TEMPLATE_DETAILS[o.value];
      if (!detail) continue;
      // The TTS parent card represents the whole 7/8/9 family — give it a
      // generic label rather than "TTS Simple IVR" (which is a sub-flavor).
      const label = o.value === '7' ? 'TTS IVR' : o.label;
      out.push({ value: o.value, label, ...detail });
    }
    return out;
  }, [isContinuous, noTtsModule, hasCustomIvrModule, lockTemplate]);

  /** The TTS family (Simple/DTMF/Call-Patch) shares one parent card. */
  const isTtsFamily =
    values.templateId === '7' ||
    values.templateId === '8' ||
    values.templateId === '9' ||
    values.templateId === '10';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Campaign basics</CardTitle>
          <CardDescription>Give your campaign a name and pick its type.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">
              Campaign name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="campaign-name"
              value={values.campaignName}
              onChange={(e) => setValues({ campaignName: e.target.value })}
              placeholder="e.g. Diwali promo · 2026-06-12"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>
              Campaign type
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <div
              role="radiogroup"
              aria-label="Campaign type"
              className={cn(
                'grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5',
                lockTemplate && 'opacity-60',
              )}
            >
              {availableCards.map((card) => {
                const Icon = card.icon;
                // The TTS parent card highlights for any of 7/8/9.
                const active = card.value === '7' ? isTtsFamily : values.templateId === card.value;
                return (
                  <button
                    key={card.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={lockTemplate}
                    title={card.description}
                    onClick={() =>
                      setValues({
                        // Clicking "TTS" defaults to TTS Simple (7); other
                        // cards write their own templateId.
                        templateId: card.value,
                        ttsId: '',
                        templateIdByTTS: '',
                        selectedTTSFlowId: '',
                      })
                    }
                    className={cn(
                      'group relative flex flex-col items-center gap-1 rounded-md border px-2 pb-2 pt-3 text-center transition-all',
                      active
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                        : 'border-border hover:border-primary/40 hover:bg-accent/30',
                      lockTemplate && 'pointer-events-none',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border transition-colors',
                        active ? 'border-primary bg-primary' : 'border-muted-foreground/40',
                      )}
                    />
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex min-h-[2.75rem] flex-col items-center justify-center gap-0.5">
                      <div className="text-sm font-semibold leading-tight text-foreground">
                        {card.label}
                      </div>
                      <div className="text-[11px] leading-tight text-muted-foreground">
                        {card.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {lockTemplate && (
              <p className="text-xs text-muted-foreground">
                Campaign type can&apos;t be changed after creation.
              </p>
            )}
            {availableCards.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No campaign types are enabled on your account. Ask an admin to enable a module.
              </p>
            )}
          </div>

          {isTtsFamily && (
            <>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <Label className="flex items-center gap-1.5">
                    <ListFilter className="h-3.5 w-3.5 text-muted-foreground" />
                    Filter flows by type
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    Only approved flows of the selected type are listed below.
                  </span>
                </div>
                <div
                  role="radiogroup"
                  aria-label="Filter TTS flows by type"
                  className="grid grid-cols-1 gap-1 rounded-lg bg-muted p-1 sm:grid-cols-3"
                >
                  {flavorOptions.map((opt) => {
                    const activeTemplateId = isContinuous
                      ? values.templateIdByTTS
                      : values.templateId;

                    const active = activeTemplateId === opt.value;
                    const FlavorIcon = FLAVOR_ICONS[String(opt.value)] ?? AudioLines;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() =>
                          setValues(
                            isContinuous
                              ? {
                                  templateIdByTTS: opt.value,
                                  selectedTTSFlowId: '',
                                }
                              : {
                                  templateId: opt.value,
                                  ttsId: '',
                                },
                          )
                        }
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active
                            ? 'bg-background text-foreground shadow-sm ring-1 ring-primary/40'
                            : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
                            active
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background text-muted-foreground',
                          )}
                        >
                          <FlavorIcon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium leading-tight">
                            {opt.label}
                          </span>
                          <span className="block truncate text-[11px] leading-tight text-muted-foreground">
                            {opt.desc}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tts-flow">
                  TTS Flow
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <Select
                      value={isContinuous ? values.selectedTTSFlowId : values.ttsId}
                      onValueChange={(val) =>
                        setValues({
                          ...(isContinuous ? { selectedTTSFlowId: val } : { ttsId: val }),
                        })
                      }
                      disabled={ttsFlows.isLoading}
                    >
                      <SelectTrigger id="tts-flow">
                        <SelectValue
                          placeholder={
                            ttsFlows.isLoading
                              ? 'Loading flows…'
                              : flowOptions.length === 0
                                ? `No approved ${templateLabel(values.templateId)} flows`
                                : 'Select a flow'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {flowOptions.length === 0 ? (
                          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                            No approved {templateLabel(values.templateId)} flows yet — create one in
                            TTS Studio first.
                          </div>
                        ) : (
                          flowOptions.map((c) => (
                            <SelectItem key={String(c.id)} value={String(c.id)} className="py-2">
                              <div className="flex w-full items-center justify-between gap-3">
                                <span className="flex min-w-0 items-center gap-2">
                                  <AudioLines className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <span className="truncate font-medium">{c.ttsFlowName}</span>
                                </span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedFlow && (
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 gap-1.5"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye className="h-4 w-4" />
                      Preview
                    </Button>
                  )}
                </div>

                {!selectedFlow && !ttsFlows.isLoading && flowOptions.length > 0 && (
                  <p className="text-xs text-muted-foreground">Select a TTS flow to continue.</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {previewOpen && selectedFlow && (
        <TtsFlowPreviewModal
          key={selectedFlow.id}
          rowId={Number(selectedFlow.id)}
          flowName={selectedFlow.ttsFlowName}
          templateId={String(selectedFlow.templateId)}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
